import React from 'react';

import { calendarStrings } from '@data/calendar';
import { CurrencyRate } from '@models/CurrencyRate';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { iota } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';

const strings = createStrings({
  ja: {
    calendarDateUSD: '1 USDは',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  calendarTable: {
    ...common.middleText,
    ...common.flexColumn,
    ...common.textGray800,
  },
  calendarRow: {
    ...common.flexInline,
  },
  calendarColumn: {
    flex: 1,
  },
  calendarHeader: {},
  calendarHeaderCell: {
    paddingBottom: 8,
  },
  calendarDate: {
    padding: 8,
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    '&:last-child': {
      borderRight: 0,
    },
    '$calendarRow:last-child &': {
      borderBottom: 0,
    },
    '&:hover, &$selected': {
      backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    },
  },
  calendarBody: {
    ...common.flexColumn,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  today: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
  },
  dateDigit: {
    ...common.flexCenter,
    ...common.fontWeight500,
    height: 24,
    width: 24,
    borderRadius: '50%',
  },
  selected: {},
}));

export interface ExchangeCalendarProps {
  date: Date;
  onClick?: (date: Date) => void;
  items: CurrencyRate[];
}

export const ExchangeCalendar: React.FC<ExchangeCalendarProps> = (props) => {
  const { date, onClick, items } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const monthStart = dayjs(date).startOf('month').startOf('day');
  const monthEnd = dayjs(date).endOf('month').startOf('day');

  const startDate = dayjs(monthStart).subtract((dayjs(monthStart).day() + 6) % 7, 'day');
  const endDate = dayjs(monthEnd).add(6 - ((dayjs(monthEnd).day() + 6) % 7), 'day');

  const days = endDate.diff(startDate, 'day') + 1;
  const rows = Math.ceil(days / 7);

  return (
    <div className={classes.calendarTable}>
      <div className={clsx(classes.calendarRow, classes.calendarHeader)}>
        {iota(7).map((i) => (
          <div key={i} className={clsx(classes.calendarColumn, classes.calendarHeaderCell)}>
            {calendarStrings.weekdaysShort((i + 1) % 7)}
          </div>
        ))}
      </div>
      <div className={clsx(classes.calendarBody)}>
        {iota(rows).map((i) => (
          <div key={i} className={clsx(classes.calendarRow)}>
            {iota(7).map((j) => {
              const currentDate = startDate.add(7 * i + j, 'day');
              const d = currentDate.get('date');
              const thisMonth = currentDate.get('month') === date.getMonth();
              const today = currentDate.isSame(new Date(), 'date');
              const selected = currentDate.isSame(date, 'date');

              const item = items.find((i) => dayjs(i.date).isSame(currentDate, 'date'));

              const rate = item ? item.rate.toFixed(2) : '-';

              return (
                <div
                  key={j}
                  className={clsx(
                    classes.calendarColumn,
                    classes.calendarDate,
                    selected && item && classes.selected,
                    !selected && item && classes.baseButton,
                  )}
                  onClick={() => item && onClick?.(currentDate.toDate())}
                >
                  <div
                    className={clsx(
                      classes.dateDigit,
                      !thisMonth && classes.textGray500,
                      today && classes.today,
                    )}
                  >
                    {String(d).padStart(2, '0')}
                  </div>
                  <div style={{ height: 13 }} />
                  <div className={clsx(classes.exSmallerText, classes.textGray500)}>
                    {strings.calendarDateUSD}
                  </div>
                  <div>
                    <span
                      className={clsx(
                        classes.largeText,
                        classes.fontWeight500,
                        (!item || !thisMonth) && classes.textGray400,
                      )}
                    >
                      {rate}
                    </span>{' '}
                    <span className={clsx(classes.exSmallerText, classes.textGray500)}>JPY</span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
